import { useQueryClient } from '@mint-lib/query-client';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';

import * as API from '../../api/index.js';
import { useEnhancedMutation, useTypedQuery } from '../../hooks/index.js';
import { useDataFilePreviewQueryOptions } from './preview.js';

export const useDataFileFormats = (
  dataFileId: string,
  options?: { throwOnError?: boolean },
) => {
  const queryOptions = DataIngestion.DataFile.getDataFileFormatsQueryOptions({
    urlParams: { id: dataFileId },
  });

  return useTypedQuery({
    ...queryOptions,
    placeholderData: { dateOrder: null, numberFormat: null },
    staleTime: 30 * 1000,
    ...options,
  });
};

export const useDataFileFormatsOptions = (options?: {
  throwOnError?: boolean;
}) => {
  const queryOptions =
    DataIngestion.DataFile.getHeadersFormatsOptionsQueryOptions({});

  return useTypedQuery({
    ...queryOptions,
    placeholderData: { dateOrders: [], numberFormats: [] },
    refetchOnMount: false,
    ...options,
  });
};

export const useUpdateDataFileFormats = (dataFileId: string) => {
  const queryClient = useQueryClient();

  const queryKey = DataIngestion.DataFile.getDataFileFormatsQueryOptions({
    urlParams: { id: dataFileId },
  }).queryKey;

  return useEnhancedMutation({
    mutationFn: API.updateFormats,
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey });

      const previousValue = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, vars.payload);

      return { previousValue };
    },

    onError: (_err, _vars, context) => {
      queryClient.setQueryData(queryKey, context?.previousValue);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    meta: {
      getErrorsMap(t) {
        return {
          default: t('Failed to select format'),
        };
      },
    },
  });
};

export const useTableDataFileHeaderRows = (
  dataFileId: string,
  options?: { throwOnError?: boolean },
) => {
  const queryOptions =
    DataIngestion.DataFile.getDataFilePreviewRowsQueryOptions({
      urlParams: { id: dataFileId },
    });

  return useTypedQuery({
    ...queryOptions,
    placeholderData: { items: [] },
    staleTime: 30 * 1000,
    ...options,
  });
};

export const useTableDataFileFooterRows = (
  dataFileId: string,
  options?: { enabled?: boolean; throwOnError?: boolean },
) => {
  const queryOptions =
    DataIngestion.DataFile.getTableDataFileFooterRowsQueryOptions({
      urlParams: { dataFileId },
    });

  return useTypedQuery({
    ...queryOptions,
    placeholderData: { items: [] },
    ...options,
  });
};

export const useUpdateDataFileHeader = (dataFileId: string) => {
  const queryClient = useQueryClient();
  const headerRowsQueryKey =
    DataIngestion.DataFile.getDataFilePreviewRowsQueryOptions({
      urlParams: { id: dataFileId },
    }).queryKey;
  const footerRowsQueryKey =
    DataIngestion.DataFile.getTableDataFileFooterRowsQueryOptions({
      urlParams: { dataFileId },
    }).queryKey;

  return useEnhancedMutation({
    mutationFn: API.updateTableDataFileHeader,
    onMutate: async ({ payload }) => {
      await queryClient.cancelQueries({ queryKey: headerRowsQueryKey });
      queryClient.removeQueries({ queryKey: footerRowsQueryKey });

      const previousValue =
        queryClient.getQueryData<DataIngestion.DataFile.PreviewRowsQueryResult>(
          headerRowsQueryKey,
        );

      if (previousValue) {
        queryClient.setQueryData<DataIngestion.DataFile.PreviewRowsQueryResult>(
          headerRowsQueryKey,
          {
            ...previousValue,
            items: previousValue.items.map((row) => ({
              ...row,
              isHeader: row.id === payload.rowId,
            })),
          },
        );
      }

      return { previousValue };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(headerRowsQueryKey, context?.previousValue);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: headerRowsQueryKey });
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t('Failed to select header'),
      }),
    },
  });
};

export const useSetFooterRowsCount = (dataFileId: string) => {
  const queryClient = useQueryClient();

  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  return useEnhancedMutation({
    mutationFn: ({ footerRowsCount }: { footerRowsCount: number }) => {
      return DataIngestion.DataFile.setFooterRowsCount({
        urlParams: { dataFileId },
        data: { footerRowsCount },
      });
    },

    onMutate: async ({ footerRowsCount }) => {
      await queryClient.cancelQueries({ queryKey: previewQueryKey });
      const previousPreview = queryClient.getQueryData(previewQueryKey);

      if (previousPreview) {
        queryClient.setQueryData(previewQueryKey, {
          ...previousPreview,
          footerRowsCount,
        });
      }

      return { previousPreview };
    },

    onError: (_err, _variables, context) => {
      if (context?.previousPreview) {
        queryClient.setQueryData(previewQueryKey, context.previousPreview);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: previewQueryKey });
    },

    meta: {
      getErrorsMap: (t) => ({
        InvalidFooterRowsCountError: t(
          'Invalid footer rows count. Must be between 0 and 40',
        ),
        default: t('Failed to set footer rows count'),
      }),
    },
  });
};
